import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import { useNotificationContext } from '../contexts/NotificationContext';

export default function FeedbackModal({ isOpen, onClose, ticketId, onFeedbackSubmitted, existingFeedback = null, isResolving = false }) {
    const [calificacion, setCalificacion] = useState(0);
    const [calificacionRapidez, setCalificacionRapidez] = useState(0);
    const [calificacionAtencion, setCalificacionAtencion] = useState(0);
    const [comentario, setComentario] = useState('');
    const [loading, setLoading] = useState(false);
    const { alertSuccess, alertError } = useNotificationContext();

    const isEditing = !!existingFeedback;

    // Precargar valores cuando se está editando
    useEffect(() => {
        if (existingFeedback) {
            setCalificacion(existingFeedback.calificacion || 0);
            setCalificacionRapidez(existingFeedback.calificacionRapidez || 0);
            setCalificacionAtencion(existingFeedback.calificacionAtencion || 0);
            setComentario(existingFeedback.comentario || '');
        } else {
            // Resetear valores si no hay feedback existente
            setCalificacion(0);
            setCalificacionRapidez(0);
            setCalificacionAtencion(0);
            setComentario('');
        }
    }, [existingFeedback, isOpen]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (calificacion === 0) {
            alertError('Por favor selecciona una calificación general.');
            return;
        }

        try {
            setLoading(true);

            if (isEditing) {
                // Actualizar feedback existente
                await api.put(`/tickets/${ticketId}/feedback`, {
                    calificacion,
                    rating: calificacion,
                    calificacionRapidez: calificacionRapidez || null,
                    calificacionAtencion: calificacionAtencion || null,
                    comentario
                });
                alertSuccess('Calificación actualizada correctamente!');
            } else {
                // Crear nuevo feedback
                await api.post(`/tickets/${ticketId}/feedback`, {
                    calificacion,
                    rating: calificacion,
                    calificacionRapidez: calificacionRapidez || null,
                    calificacionAtencion: calificacionAtencion || null,
                    comentario
                });

                // Si es un nuevo feedback, cerramos el ticket
                try {
                    await api.put(`/tickets/${ticketId}/estado`, { estado: 'Cerrado' });
                } catch (e) { console.error('Error cerrando ticket via feedback', e); }

                alertSuccess(isResolving ? 'Ticket solucionado y cerrado correctamente.' : 'Gracias por tu feedback! Ticket cerrado.');
            }

            onFeedbackSubmitted(isResolving ? { estado: 'Cerrado' } : null);
            onClose();
        } catch (error) {
            console.error('Error enviando feedback:', error);
            alertError(error.response?.data?.message || (typeof error.response?.data === 'string' ? error.response?.data : 'Error al enviar feedback.'));
        } finally {
            setLoading(false);
        }
    };

    const renderStars = (value, setValue, label) => (
        <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
            <div className="flex space-x-1">
                {[1, 2, 3, 4, 5].map((star) => (
                    <button
                        key={star}
                        type="button"
                        onClick={() => setValue(star)}
                        className={`text-2xl focus:outline-none transition-colors ${star <= value ? 'text-yellow-400' : 'text-gray-300 dark:text-gray-600'
                            }`}
                    >
                        ★
                    </button>
                ))}
            </div>
        </div>
    );

    return createPortal(
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                    <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
                </div>

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                    <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div className="sm:flex sm:items-start">
                            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4" id="modal-title">
                                    {isResolving ? 'Confirmar Solución y Calificar Atención' : isEditing ? 'Editar calificación' : 'Califica la atención recibida'}
                                </h3>
                                <div className="mt-2">
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                                        Tu opinión es muy importante para mejorar nuestro servicio.
                                    </p>

                                    {renderStars(calificacion, setCalificacion, "Calificación General")}

                                    <div className="grid grid-cols-2 gap-4">
                                        {renderStars(calificacionRapidez, setCalificacionRapidez, "Rapidez")}
                                        {renderStars(calificacionAtencion, setCalificacionAtencion, "Atención")}
                                    </div>

                                    <textarea
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white mt-2"
                                        rows="4"
                                        placeholder="Comentarios adicionales (opcional)..."
                                        value={comentario}
                                        onChange={(e) => setComentario(e.target.value)}
                                    ></textarea>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={loading}
                            className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 ${isResolving ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'} text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50`}
                        >
                            {loading ? 'Enviando...' : (isResolving ? 'Confirmar y Enviar Calificación' : isEditing ? 'Actualizar Calificación' : 'Enviar Calificación')}
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
